import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  addTemplateApi,
  fetchTemplateByIdApi,
  updateTemplateApi,
} from "../api/template";
import toast from "react-hot-toast";
import Editor from "./Editor";

interface TemplateFormProps {
  url: string;
}

export const TemplateForm = ({ url }: TemplateFormProps) => {
  const [name, setName] = useState("");
  const [content, setContent] = useState("");
  const placeholder = "";

  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [, setTid] = useState<string>("");

  const config: Record<string, any> = useMemo(
    () => ({
      readonly: false,
      placeholder: placeholder || "",
    }),
    [placeholder]
  );

  const validateForm = () => {
    if (!name || !content) {
      toast.error("Please fill in all required fields: Name and Content.");
      return false;
    }
    return true;
  };

  const queryParams = new URLSearchParams(window.location.search);
  const id = queryParams.get("id");

  useEffect(() => {
    if (id) {
      const fetchTemplateData = async () => {
        try {
          const fetchedUser = await fetchTemplateByIdApi(url, id);
          if (fetchedUser) {
            const user = fetchedUser as {
              name: string;
              content: string;
            };
            setIsEditMode(true);
            setLoading(false);
            setName(user.name);
            setContent(user.content);
          }
        } catch (error) {
          console.error("Unable to fetch user data", error);
          toast.error("Unable to fetch user data", { id: "fetchError" });
          setLoading(false);
          setHasError(true);
        }
      };
      fetchTemplateData();
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      setTid(id);
      setIsEditMode(true);
      setLoading(true);
    } else {
      setIsEditMode(false);
      setLoading(false);
    }
  }, [id]);

  const handleSubmit = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (!validateForm()) return;

    try {
      if (isEditMode) {
        await updateTemplateApi(url, id, name, content);
        toast.success("Template updated successfully");
      } else {
        await addTemplateApi(url, name, content);
        toast.success("Template added successfully");
      }
      window.history.pushState({}, "", "/admin/template");
      window.dispatchEvent(new PopStateEvent("popstate"));
    } catch (error) {
      console.error("Error adding/updating Template:", error);
      toast.error(
        isEditMode ? "Failed to update Template" : "Failed to add Template"
      );
    }
  };

  const handleCancelClick = () => {
    setName("");
    setContent("");

    window.history.pushState({}, "", "/admin/template");
    window.dispatchEvent(new PopStateEvent("popstate"));
  };

  return (
    <div>
      <div>
        {loading ? (
          <div className="flex justify-center items-center h-screen">
            <div
              className="flex justify-center items-center h-12 w-12 animate-spin rounded-full border-4 border-solid border-current border-e-transparent align-[-0.125em] text-surface motion-reduce:animate-[spin_1.5s_linear_infinite] text-blue-600"
              role="status"
            >
              <span className="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]">
                Loading...
              </span>
            </div>
          </div>
        ) : hasError ? (
          <div className="flex justify-center items-center h-screen">
            {/* Blank page when there’s an error */}
          </div>
        ) : (
          <div className="w-full pr-16">
            <div className="flex-grow p-6">
              <form>
                <h1 className="text-3xl font-bold text-blue-600 capitalize dark:text-white mb-4">
                  {isEditMode ? "Edit Template" : "Add Template"}
                </h1>

                <div className="mb-6 w-full">
                  <label className="text-gray-900 dark:text-gray-200 font-semibold">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    required
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-4 py-2 mt-2  text-gray-800  bg-white border border-gray-400 rounded-md dark:bg-gray-800 dark:text-gray-300 dark:border-gray-800 focus:border-blue-400 focus:ring-blue-300 focus:ring-opacity-40 dark:focus:border-blue-300 focus:outline-none focus:ring"
                    placeholder="Enter Name"
                  />
                </div>
                <div className="mb-6 w-full">
                  <label className="text-gray-900 dark:text-gray-200 font-semibold">
                    Content <span className="text-red-500">*</span>
                  </label>
                  <Editor
                    content={content}
                    setContent={setContent}
                    config={config}
                  />
                </div>

                <div className="flex space-x-4 mt-6 justify-start">
                  <button
                    type="submit"
                    onClick={handleSubmit}
                    className="px-8 py-2.5 leading-5 text-white transition-colors duration-300 transform bg-blue-600 rounded-md hover:bg-blue-500 focus:outline-none focus:bg-gray-600"
                  >
                    {isEditMode ? "Update" : "Save and Close"}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancelClick}
                    className="px-8 py-2.5 leading-5 text-white transition-colors duration-300 transform bg-blue-600 rounded-md hover:bg-blue-500 focus:outline-none focus:bg-gray-600"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
